// src/pages/FeedPage.tsx
//
// Realtime feed with three tabs:
//
//   • Following — items from accounts the viewer follows.
//   • Yours     — items the viewer created themselves.
//   • Public    — public items from anyone on the platform.
//
// Active tab is mirrored in the URL (?tab=following|yours|public)
// so refreshes preserve state and intra-app links can deep-link
// to a specific tab. Default = Following on first load (matches
// the previous single-tab behavior so returning users land where
// they did before).
//
// Empty states are tab-specific: Following nudges "follow people,"
// Yours nudges "save a moment from chat," Public nudges "be the
// first to share." Loading/error states are shared across tabs.

import React from 'react';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  DynamicFeedOutlined,
  PeopleOutline,
  PersonOutline,
  PublicOutlined,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';

import { useT } from '../i18n/useT';
import { useFeed, FeedMode } from '../feed/useFeed';
import { FeedItemCard } from '../components/feed/FeedItemCard';

// Bridge: the hook calls the personal-timeline mode 'timeline'
// internally; the URL/UI exposes it as 'yours' for users. Keep
// one canonical name on each side and translate at the boundary.
const URL_TO_MODE: Record<string, FeedMode> = {
  following: 'following',
  yours: 'timeline',
  public: 'public',
};
const MODE_TO_URL: Record<FeedMode, string> = {
  following: 'following',
  timeline: 'yours',
  public: 'public',
};

export const FeedPage: React.FC = () => {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();

  // Resolve mode from URL, defaulting to following. Unknown values
  // fall back to following too so a stale link never breaks the
  // page.
  const tabParam = (searchParams.get('tab') || 'following').toLowerCase();
  const mode: FeedMode = URL_TO_MODE[tabParam] ?? 'following';

  const { items, loading, error } = useFeed(mode);

  const handleTabChange = (_: React.SyntheticEvent, next: FeedMode) => {
    const urlValue = MODE_TO_URL[next];
    // Replace (not push) so the back button doesn't snake through
    // every tab the user touched — they expect back to leave the
    // feed entirely, not iterate tabs.
    setSearchParams({ tab: urlValue }, { replace: true });
  };

  return (
    // Single outer scroll container with NO padding — the entire
    // column height is the scroll viewport. This way as cards scroll
    // up they pass behind both the floating frosted header (top) and
    // the floating frosted bottom nav. The padding is on the
    // INTERNAL scroll content instead: a top spacer pushes title +
    // tabs below the header, a bottom spacer keeps the last card
    // visible above the bottom nav. (Putting padding on the
    // container would create empty zones behind the bars and
    // backdrop-filter would have nothing varied to blur.)
    <Box sx={{ height: '100%', overflowY: 'auto' }}>
      <Box sx={{ paddingTop: 'var(--app-header-h)' }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('Feed')}
          </Typography>
        </Box>

        <Tabs
          value={mode}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontSize: '0.85rem',
              fontWeight: 500,
              gap: 0.75,
            },
          }}
        >
          <Tab
            value={'following' as FeedMode}
            icon={<PeopleOutline sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={t('Following')}
          />
          <Tab
            value={'timeline' as FeedMode}
            icon={<PersonOutline sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={t('Yours')}
          />
          <Tab
            value={'public' as FeedMode}
            icon={<PublicOutlined sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={t('Public')}
          />
        </Tabs>
      </Box>

      <Box sx={{ paddingBottom: 'var(--app-bottom-nav-h)' }}>
        <FeedTabContent
          mode={mode}
          items={items}
          loading={loading}
          error={error}
        />
      </Box>
    </Box>
  );
};

// Per-tab body — handles loading, error, empty, and populated.
// Pulled out so the parent can keep tab chrome stable while the
// body re-mounts on tab change.
const FeedTabContent: React.FC<{
  mode: FeedMode;
  items: ReturnType<typeof useFeed>['items'];
  loading: boolean;
  error: string | null;
}> = ({ mode, items, loading, error }) => {
  const t = useT();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '40vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          {t('Could not load your feed.')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return <FeedEmptyState mode={mode} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 2,
        py: 2,
      }}
    >
      {items.map((item) => (
        <FeedItemCard key={item.id} item={item} />
      ))}
    </Box>
  );
};

const FeedEmptyState: React.FC<{ mode: FeedMode }> = ({ mode }) => {
  const t = useT();
  const { title, body } = (() => {
    if (mode === 'following') {
      return {
        title: t('Nothing from your follows yet'),
        body: t(
          "When you follow other travelers, what they share will show up here. Try the Public tab to discover people to follow."
        ),
      };
    }
    if (mode === 'timeline') {
      return {
        title: t('Your timeline lives here'),
        body: t(
          "Save a moment from chat, snap a photo at a landmark, or thumbs-up a message you want to come back to. They'll all show up here in order."
        ),
      };
    }
    return {
      title: t('Nothing public yet'),
      body: t(
        "Be the first to share something publicly. Tap the menu on any of your posts and set visibility to Public."
      ),
    };
  })();

  return (
    <Box sx={{ p: 3, textAlign: 'center', pb: 10 }}>
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
        }}
      >
        <DynamicFeedOutlined
          sx={{ fontSize: 40, color: 'text.secondary' }}
        />
      </Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ maxWidth: 340, mx: 'auto' }}
      >
        {body}
      </Typography>
    </Box>
  );
};
